/**
 * Сотрудники админки: `GET/POST /admin/staff`, `GET/PUT/DELETE /admin/staff/{id}`,
 * сброс пароля и активные сеансы.
 *
 * Временный пароль сервер показывает **один раз** — при создании сотрудника и
 * при сбросе. Второго шанса нет, поэтому экран обязан показать его сразу
 * (docs/frontend/README.md › Admin: staff and pharmacies).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { parseMs as toMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type StaffDto = components['schemas']['Staff']
type SessionDto = components['schemas']['Session']

export interface Staff {
  id: number
  name: string
  phone: string
  email: string
  roleId: number
  roleName: string
  /** 0 — вся сеть; иначе аптека, которой ограничен сотрудник (R-08). */
  storeId: number
  active: boolean
  /** Владелец: его нельзя отключить, удалить, переназначить или сбросить. */
  system: boolean
  /** Последний вход, мс; 0 — не входил ни разу. */
  lastLogin: number
  /** Пароль временный — сотрудник ещё не сменил его. */
  mustChangePassword: boolean
  /** До какого времени вход заблокирован после пяти промахов, мс; 0 — открыт. */
  lockedUntil: number
}

export interface StaffSession {
  id: string
  lastUsed: number
  expires: number
  agent: string
  ip: string
}

export function toStaff(dto: StaffDto): Staff {
  return {
    id: dto.id ?? 0,
    name: dto.name ?? '',
    phone: dto.phone ?? '',
    email: dto.email ?? '',
    roleId: dto.role_id ?? 0,
    roleName: dto.role ?? '',
    storeId: dto.store_id ?? 0,
    active: dto.is_active ?? true,
    system: dto.is_system ?? false,
    lastLogin: toMs(dto.last_login_at),
    mustChangePassword: dto.must_change_password ?? false,
    lockedUntil: toMs(dto.locked_until),
  }
}

function toSession(dto: SessionDto): StaffSession {
  return {
    id: dto.id ?? '',
    lastUsed: toMs(dto.last_used_at),
    expires: toMs(dto.expires_at),
    agent: dto.user_agent ?? '',
    ip: dto.ip ?? '',
  }
}

export interface StaffQuery {
  q?: string
  roleId?: number
  storeId?: number
}

/**
 * Справочник сотрудников целиком (до 100 — предел сервера): нужен, чтобы по
 * номеру показать имя сборщика в заказе. Для списков на экране — useStaffPage.
 */
export async function fetchStaff(api: ApiClient, query: StaffQuery = {}): Promise<Staff[]> {
  const page = await api.get<{ items?: StaffDto[] }>('/admin/staff', {
    query: {
      q: query.q || undefined,
      role_id: query.roleId || undefined,
      store_id: query.storeId || undefined,
      page_size: 100,
    },
  })
  return (page.items ?? []).map(toStaff)
}

/** Страница сотрудников: списки листаются на сервере, по 10 строк. */
export interface StaffPage {
  items: Staff[]
  total: number
}

export interface StaffPageQuery extends StaffQuery {
  page: number
  pageSize: number
}

export function useStaffPage(query: StaffPageQuery, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.staff, 'page', query],
    queryFn: async (): Promise<StaffPage> => {
      const page = await api.get<{ items?: StaffDto[]; total?: number }>('/admin/staff', {
        query: {
          q: query.q || undefined,
          role_id: query.roleId || undefined,
          store_id: query.storeId || undefined,
          page: query.page,
          page_size: query.pageSize,
        },
      })
      return { items: (page.items ?? []).map(toStaff), total: page.total ?? 0 }
    },
    placeholderData: keepPreviousData,
    enabled,
  })
}

export interface StaffInput {
  id: number
  name: string
  phone: string
  email: string
  roleId: number
  /** 0 — вся сеть. */
  storeId: number
  active: boolean
}

function toBody(staff: StaffInput) {
  return {
    name: staff.name,
    phone: staff.phone,
    email: staff.email || undefined,
    role_id: staff.roleId,
    store_id: staff.storeId || null,
    is_active: staff.active,
  }
}

export interface SavedStaff {
  staff: Staff
  /** Только для нового сотрудника: показать один раз и больше не хранить. */
  temporaryPassword: string
}

export async function saveStaff(api: ApiClient, input: StaffInput): Promise<SavedStaff> {
  if (input.id) {
    const dto = await api.put<StaffDto>(`/admin/staff/${input.id}`, toBody(input))
    return { staff: toStaff(dto), temporaryPassword: '' }
  }
  const created = await api.post<{ staff?: StaffDto; temporary_password?: string }>(
    '/admin/staff',
    toBody(input),
  )
  return {
    staff: toStaff(created.staff ?? {}),
    temporaryPassword: created.temporary_password ?? '',
  }
}

export function useStaff(query: StaffQuery = {}) {
  const api = useApi()
  // Фильтр — часть ключа: иначе список одной аптеки и весь штат сети делили
  // бы одну запись кэша, и карточка аптеки могла показать чужих сотрудников.
  // Инвалидация по `keys.staff` накрывает все варианты.
  return useQuery({
    queryKey: [...keys.staff, 'list', query],
    queryFn: () => fetchStaff(api, query),
  })
}

/** Один сотрудник — для его карточки (`GET /admin/staff/{id}`). */
export function useStaffMember(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.staffMember(id),
    queryFn: async () => toStaff(await api.get<StaffDto>(`/admin/staff/${id}`)),
    enabled: id > 0,
  })
}

export function useStaffSessions(id: number, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.staffSessions(id),
    queryFn: async () => {
      const page = await api.get<{ items?: SessionDto[] }>(`/admin/staff/${id}/sessions`)
      return (page.items ?? []).map(toSession)
    },
    enabled,
  })
}

export function useSaveStaff() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: StaffInput) => saveStaff(api, input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.staff }),
  })
}

export function useResetPassword() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const done = await api.post<{ temporary_password?: string }>(
        `/admin/staff/${id}/password-reset`,
      )
      return done.temporary_password ?? ''
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.staff }),
  })
}

export function useEndSessions() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/admin/staff/${id}/sessions`),
    onSuccess: (_data, id) => client.invalidateQueries({ queryKey: keys.staffSessions(id) }),
  })
}

export function useDeleteStaff() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/admin/staff/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.staff }),
  })
}
